// src/components/OrderTypeSelector/index.js - FIXED PRE-ORDER DISPLAY
import React, { useState, useEffect } from 'react';
import { X, Truck, ShoppingBag, MapPin, Clock, AlertCircle, Calendar } from 'lucide-react';
import { settingsService } from '../../config/settingsService';
import './index.css';

const OrderTypeSelector = ({
  isOpen,
  onClose,
  selectedStore,
  onContinue,
  userLocation,
  isPreOrder = false,
  outOfRangeData = null
}) => {
  const [error, setError] = useState('');

  // Check if services are enabled
  const baseDeliveryEnabled = settingsService.isDeliveryEnabled();
  const isTakeawayEnabled = settingsService.isTakeawayEnabled();

  const outOfRange = outOfRangeData?.outOfRange === true;
  const isDeliveryEnabled = baseDeliveryEnabled && !outOfRange;

  useEffect(() => {
    console.log('🎯 OrderTypeSelector mounted/updated:', {
      isOpen,
      storeName: selectedStore?.name,
      isPreOrder,
      storeSupportsPreOrder: selectedStore?.allow_pre_order,
      isDeliveryEnabled,
      isTakeawayEnabled,
      outOfRangeData
    });
  }, [isOpen, selectedStore, isPreOrder, outOfRangeData]);

  useEffect(() => {
    if (!isOpen) {
      setError('');
    }
  }, [isOpen]);

  if (!isOpen || !selectedStore) return null;

  const handleTypeSelect = (type) => {
    setError('');

    // Safety check in case user clicks disabled element
    if (type === 'delivery' && !isDeliveryEnabled) {
      if (outOfRange) {
        setError(`Delivery is unavailable beyond our ${outOfRangeData.radius}km radius`);
      } else {
        setError('Delivery is currently unavailable');
      }
      return;
    }
    if (type === 'collection' && !isTakeawayEnabled) {
      setError('Pickup is currently unavailable');
      return;
    }

    console.log('🎯 OrderTypeSelector: Order type selected:', {
      type,
      isPreOrder,
      storeName: selectedStore.name
    });

    if (type === 'delivery') {
      onContinue({
        order_type: 5,
        type: 'delivery',
        userLocation: userLocation,
        store: selectedStore,
        isPreOrder: isPreOrder
      });
    } else {
      onContinue({
        order_type: 10,
        type: 'collection',
        store: selectedStore,
        isPreOrder: isPreOrder
      });
    }
  };

  return (
    <div className="order-type-overlay" onClick={onClose}>
      <div className="order-type-modal" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="order-type-close">
          <X size={20} />
        </button>

        {/* ✅ FIXED: Prominent Pre-Order Banner at Top */}
        {isPreOrder && (
          <div className="pre-order-top-banner">
            <div className="pre-order-banner-icon">
              <Calendar size={24} />
            </div>
            <div className="pre-order-banner-content">
              <h3 className="pre-order-banner-title">🛍️ Pre-Order Mode</h3>
              <p className="pre-order-banner-subtitle">
                Schedule your order for a future date & time
              </p>
            </div>
          </div>
        )}

        <div className="order-type-header">
          <div className="order-type-store-info">
            <div className="order-type-store-name-wrapper">
              <MapPin size={16} />
              <h2 className="order-type-store-name">{selectedStore.name}</h2>
            </div>
          </div>

          <h3 className="order-type-title">
            {isPreOrder
              ? 'How would you like to receive your pre-order?'
              : 'How would you like to receive your order?'
            }
          </h3>

          {isPreOrder && (
            <p className="order-type-subtitle">
              You'll be able to schedule your preferred date and time in the next step
            </p>
          )}
        </div>

        <div className="order-type-content">
          {outOfRange && (
            <div className="out-of-range-banner">
              <AlertCircle size={20} className="out-of-range-icon" />
              <div className="out-of-range-text">
                <strong>Out of Delivery Range</strong>
                <p>Cannot deliver to your location as it is beyond our {outOfRangeData?.radius || 50}km delivery radius.</p>
              </div>
            </div>
          )}

          {/* Delivery Card */}
          <div
            className={`order-type-card ${!isDeliveryEnabled ? 'disabled' : ''}`}
            onClick={() => handleTypeSelect('delivery')}
            style={{ opacity: isDeliveryEnabled ? 1 : 0.6, cursor: isDeliveryEnabled ? 'pointer' : 'not-allowed' }}
          >
            <div className="order-type-card-header">
              <div className="order-type-card-left">
                <div className="order-type-icon">
                  <Truck size={24} />
                </div>
                <div className="order-type-info">
                  <h4 className="order-type-name">
                    {isPreOrder ? '📅 Schedule Delivery' : 'Home Delivery'}
                  </h4>
                  <p className="order-type-status">
                    {outOfRange
                      ? 'Unavailable at this distance'
                      : (isPreOrder ? 'Choose your delivery date & time' : 'Get it delivered to your doorstep')}
                  </p>
                </div>
              </div>
              <div className={`order-type-badge ${isDeliveryEnabled ? 'available' : 'unavailable'}`}>
                {outOfRange ? 'Out of Range' : (isDeliveryEnabled ? 'Available' : 'Unavailable')}
              </div>
            </div>
            <div className="order-type-card-body">

              {!isPreOrder && selectedStore.distance !== undefined && (
                <div className="order-type-detail">
                  <MapPin size={16} className="order-type-detail-icon" />
                  <span>
                    {selectedStore.distance < 1
                      ? `${Math.round(selectedStore.distance * 1000)}m away`
                      : `${selectedStore.distance.toFixed(1)}km away`
                    }
                  </span>
                </div>
              )}
              {isPreOrder && (
                <div className="order-type-highlight">
                  <Calendar size={16} />
                  <span>Perfect for future events & parties</span>
                </div>
              )}
            </div>
          </div>

          {/* Collection Card */}
          <div
            className={`order-type-card ${!isTakeawayEnabled ? 'disabled' : ''}`}
            onClick={() => isTakeawayEnabled && handleTypeSelect('collection')}
            style={{ opacity: isTakeawayEnabled ? 1 : 0.6, cursor: isTakeawayEnabled ? 'pointer' : 'not-allowed' }}
          >
            <div className="order-type-card-header">
              <div className="order-type-card-left">
                <div className="order-type-icon">
                  <ShoppingBag size={24} />
                </div>
                <div className="order-type-info">
                  <h4 className="order-type-name">
                    {isPreOrder ? '📅 Schedule Pickup' : 'Pickup / Collection'}
                  </h4>
                  <p className="order-type-status">
                    {isPreOrder
                      ? 'Choose your pickup date & time'
                      : 'Collect from store'
                    }
                  </p>
                </div>
              </div>
              <div className={`order-type-badge ${isTakeawayEnabled ? 'available' : 'unavailable'}`}>
                {isTakeawayEnabled ? 'Available' : 'Unavailable'}
              </div>
            </div>
            <div className="order-type-card-body">

              <div className="order-type-detail">
                <MapPin size={16} className="order-type-detail-icon" />
                <span>Pickup from {selectedStore.location}</span>
              </div>
              {isPreOrder && (
                <div className="order-type-highlight">
                  <Calendar size={16} />
                  <span>Schedule and collect at your convenience</span>
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="order-type-error-message">
              <AlertCircle size={16} className="order-type-error-icon" />
              <span>{error}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderTypeSelector;